// Sing in Solfège phrases and rules (issue #3722): the hymn phrases a session asks the singer to
// sing in syllables, when each note sounds, how a sung note is graded, how a session is scored, and
// where do sits for each phrase in a singer's range. Pure functions and plain data shared by the
// session reducer, the page and the stories. No DOM, no audio.
//
// A phrase is a well-known line of a hymn the singer is likely to know (its opening, a refrain, its
// last phrase), the melody only, written as scale degrees from do in the convention
// interval_training.ts uses (0 do, 4 sol, -3 the sol below, 7 the do above), so its syllables and
// its pitches once do is chosen come from the same functions as Interval Training's. A note may be
// raised or lowered a semitone (fi, te), and a phrase may be minor, sung in do-based minor (issue
// #3772): such a phrase is Medium at least, and its new syllables are taught before singers meet
// them (issue #3785). The module never names a key, an interval, a staff letter or a lyric (issue
// #3700): the hymn's title and which phrase of it this is ("Opening phrase", "Last phrase") are all
// the screens say about it.
//
// Unlike Interval Training the singer is not made to get each note right before moving on: the
// phrase runs in tempo from a one-bar count-in and never waits, and each note is graded afterwards
// by the share of its duration the voice was on or near the note. A grading window starts late, by
// the voice's lag and a short settle while the voice travels to the note, and a note too short to
// grade fairly at its phrase's tempo is a passing note: drawn, never graded, never counted.
//
// Each phrase gets its own do, placed in the singer's range by Interval Training's home-note rule
// (homeNoteCandidates / pickHomeNote), so the trim, the C, F or G do and the staff's limit, dropped
// before the range is, hold here too. A phrase that cannot fit even the range as measured is left
// out of that singer's session. Only when no phrase of the level fits is the deck served as it is,
// each phrase then placed as close to the range as it can be.
//
// Every phrase lives in one list, and each level's deck is drawn from it by the phrase's difficulty
// (solfegePhraseDifficulty): how often and how far the melody jumps, and how tricky its rhythm is.
// Within a level a session serves the lines singers know best first (pickSolfegePhrases): openings
// to begin with, then last phrases as the singer gets good at the level, then lines from the middle
// of a hymn, with the openings and last phrases still coming back.

import {
  homeNotesInsideSpan,
  pickHomeNoteForSpan,
  semitonesFromDo,
  SemitoneSpan,
  SolfegeAlteration,
  SolfegeSyllable,
  solfegeSyllableFor,
  spanWithDo,
} from './interval_training.ts';
import { learnSessionScoreMax } from './learn_rules.ts';
import { LearnLevel, VocalRange } from './model.ts';
import { RandomSource, ResultColor } from './rhythm_echo.ts';
import { mulberry32 } from './util.ts';

// ---- Phrases ---------------------------------------------------------------------------------

export interface SolfegeNote {
  degree: number; // scale degree from do, as interval_training.ts counts them
  // Raised or lowered a semitone from the degree's note of the major scale (issue #3772), whatever
  // the phrase's mode: a minor tune's third is degree 2 lowered (me), and fa raised (fi) is degree
  // 3 raised. Absent for a note of the major scale, as most notes are.
  alteration?: SolfegeAlteration;
  beats: number; // duration in beats of the phrase's tempo
}

// The scale a phrase is sung in (issue #3772). Minor is do-based (the product owner, 2026-10-03): do
// is still the tune's home note, and its scale is do re me fa sol le te, the third, sixth and
// seventh lowered, with la and ti where a tune raises its sixth or seventh.
export type SolfegeMode = 'major' | 'minor';

// Which part of its hymn a phrase is (issue #3772): its opening; a closing, its last phrase or the
// start of its refrain, the two places besides the opening that singers know best; or a middle, any
// other line. A session brings the three in by turn as the singer gets good at a level
// (solfegeOpenKinds). The list sets each phrase's kind beside its label rather than reading it from
// the label, so a label can be reworded without moving the phrase; a test checks the two agree.
export type SolfegePhraseKind = 'opening' | 'closing' | 'middle';

export interface SolfegePhrase {
  title: string; // the hymn's title, the only thing on screen that says which hymn it is
  label: string; // the line under the title: which phrase of the hymn it is ("Opening phrase")
  kind: SolfegePhraseKind; // which part of the hymn the label names
  tune: string; // the tune name, for maintainers; never shown
  beatsPerBar: number; // the count-in is one bar
  bpm: number;
  notes: SolfegeNote[];
  mode?: SolfegeMode; // major when absent, as almost every phrase is
  // The level the phrase is served at when its difficulty alone would put it wrongly, for what the
  // notes cannot say (how well known the hymn is). Every use carries a comment saying why.
  level?: LearnLevel;
}

// ---- A note's syllable and pitch ---------------------------------------------------------------

// Every syllable, pitch and span of a phrase comes from these, so an altered note is a semitone off
// its degree everywhere at once and nothing works it out by hand.

// How far a note is from do in semitones, below do when negative
export function solfegeNoteSemitones(note: Pick<SolfegeNote, 'degree' | 'alteration'>): number {
  return semitonesFromDo(note.degree) + (note.alteration ?? 0);
}

// The note's pitch once do has been chosen
export function solfegeNoteMidi(homeMidi: number, note: Pick<SolfegeNote, 'degree' | 'alteration'>): number {
  return homeMidi + solfegeNoteSemitones(note);
}

// The syllable the note is sung on: fi for fa raised, me for mi lowered
export function solfegeNoteSyllable(note: Pick<SolfegeNote, 'degree' | 'alteration'>): SolfegeSyllable {
  return solfegeSyllableFor(note.degree, note.alteration ?? 0);
}

export function solfegePhraseMode(phrase: Pick<SolfegePhrase, 'mode'>): SolfegeMode {
  return phrase.mode ?? 'major';
}

// How each degree of a mode's own scale is altered from the major scale's, by degree within the
// octave: minor lowers the third, sixth and seventh
const modeAlterations: Record<SolfegeMode, SolfegeAlteration[]> = {
  major: [0, 0, 0, 0, 0, 0, 0],
  minor: [0, 0, -1, 0, 0, -1, -1],
};

// The alteration a degree has in the mode's own scale: me, le and te in minor, none in major
export function solfegeScaleAlteration(mode: SolfegeMode, degree: number): SolfegeAlteration {
  return modeAlterations[mode][((degree % 7) + 7) % 7];
}

// Whether a note is altered beyond what its mode's scale accounts for: in major any altered note;
// in minor not me, le and te, which are its scale's own, but a raised la or ti (or any other note
// raised or lowered)
export function isSolfegeChromatic(note: Pick<SolfegeNote, 'degree' | 'alteration'>, mode: SolfegeMode): boolean {
  return (note.alteration ?? 0) !== solfegeScaleAlteration(mode, note.degree);
}

// How many of the phrase's notes are chromatic (isSolfegeChromatic)
export function solfegeChromaticCount(phrase: Pick<SolfegePhrase, 'notes' | 'mode'>): number {
  const mode = solfegePhraseMode(phrase);
  return phrase.notes.filter((note) => isSolfegeChromatic(note, mode)).length;
}

// ---- Note values -----------------------------------------------------------------------------

// How a note's length is written on the staff, with the phrase's beat as the quarter note: the head
// filled or open, a stem or none, how many flags, and whether it is dotted. Tempo matters in this
// module, so the staff shows each note's real value (Michael, round 2 of issue #3722). Flags, never
// beams: each sung syllable carries its own flag, as vocal music is traditionally engraved.
export type SolfegeNoteValueName =
  'sixteenth' | 'eighth' | 'dottedEighth' | 'quarter' | 'dottedQuarter' | 'half' | 'dottedHalf' | 'whole';

export interface SolfegeNoteValue {
  name: SolfegeNoteValueName;
  beats: number;
  head: 'filled' | 'open';
  stem: boolean;
  flags: number;
  dotted: boolean;
}

// Every length the staff can write. The whole note is in no phrase of the deck today and is here so
// a phrase that holds one draws it.
export const solfegeNoteValues: readonly SolfegeNoteValue[] = [
  { name: 'sixteenth', beats: 0.25, head: 'filled', stem: true, flags: 2, dotted: false },
  { name: 'eighth', beats: 0.5, head: 'filled', stem: true, flags: 1, dotted: false },
  { name: 'dottedEighth', beats: 0.75, head: 'filled', stem: true, flags: 1, dotted: true },
  { name: 'quarter', beats: 1, head: 'filled', stem: true, flags: 0, dotted: false },
  { name: 'dottedQuarter', beats: 1.5, head: 'filled', stem: true, flags: 0, dotted: true },
  { name: 'half', beats: 2, head: 'open', stem: true, flags: 0, dotted: false },
  { name: 'dottedHalf', beats: 3, head: 'open', stem: true, flags: 0, dotted: true },
  { name: 'whole', beats: 4, head: 'open', stem: false, flags: 0, dotted: false },
];

// The value a note lasting `beats` is written as. Throws for any other length: the staff has no way
// to write it (no ties, no tuplets), so a phrase holding one must never reach a singer.
export function solfegeNoteValue(beats: number): SolfegeNoteValue {
  const value = solfegeNoteValues.find((candidate) => candidate.beats === beats);
  if (!value) {
    throw new Error(`No note value lasts ${beats} beats`);
  }
  return value;
}

// ---- Difficulty ------------------------------------------------------------------------------

// What makes a phrase hard to sing in syllables is how often and how far its melody jumps, and how
// tricky its rhythm is (the product owner's review of the Easy phrases, issue #3772): Rejoice, the
// Lord Is King is all jumps and ends on a sixth, and Joy to the World's dotted eighth and sixteenth
// are hard to land in tempo. A minor tune or a chromatic note, syllables a singer meets after the
// major scale's, puts a phrase at Medium at least (the product owner, 2026-10-03). Every number of
// the measure is here, so a change to one shows in the pinned table of sing_in_solfege.test.ts as
// exactly the phrases that move.
export const solfegeDifficultyTunables = {
  // What a move between two neighbouring notes costs, by its size in real semitones, a raised or
  // lowered note's included: do to me is a minor third, fa to fi a semitone. A repeat or a step is
  // free.
  stepMaxSemitones: 2,
  // A third. One between notes of the home chord (do, mi and sol, in any octave), which Interval
  // Training's Easy level teaches first, costs half. The chord is matched by syllable, so a minor
  // tune's me does not earn the discount today: do to me costs a whole third.
  thirdMaxSemitones: 4,
  thirdCost: 1,
  homeChordThirdCost: 0.5,
  homeChord: ['do', 'mi', 'sol'] as readonly SolfegeSyllable[],
  // A fourth or a fifth. Do to sol, either way and in any octave, is the jump every singer knows,
  // so it costs half.
  fifthMaxSemitones: 7,
  fourthOrFifthCost: 2,
  doSolCost: 1,
  // A sixth or wider
  wideLeapCost: 3,
  // What the rhythm costs: an eighth anywhere; a sixteenth or a dotted eighth anywhere, which are
  // hard to land in tempo; more than half the notes shorter than a beat; and more than
  // manyNoteValues different lengths, so the singer cannot settle into a pattern.
  eighthBeats: 0.5,
  eighthCost: 1,
  sixteenthBeats: [0.25, 0.75] as readonly number[], // a sixteenth or a dotted eighth
  sixteenthCost: 2,
  shortNoteBeats: 1, // a note shorter than this is short
  mostlyShortShare: 0.5,
  mostlyShortCost: 1,
  manyNoteValues: 3,
  manyNoteValuesCost: 1,
  // Hard: a sixth or wider anywhere, jumps on average this costly, or a busy rhythm together with a
  // fourth or a fifth that is not do to sol
  hardLargestLeap: 8,
  hardLeapLoad: 0.9,
  hardRhythmCost: 4,
  hardRhythmWithMoveCost: 2,
  // Medium: a fourth or a fifth that is not do to sol, jumps on average this costly, or a rhythm
  // past plain quarters and eighths
  mediumMoveCost: 2,
  mediumLeapLoad: 0.6,
  mediumRhythmCost: 2,
  // The least level of a minor phrase, or of one with any chromatic note, whatever its jumps and
  // rhythm: their syllables are taught before singers meet them (issue #3785), not on Easy
  minorOrChromaticLevel: LearnLevel.Medium,
};

const tunables = solfegeDifficultyTunables;

export interface SolfegePhraseDifficulty {
  leapLoad: number; // the moves' total cost over the number of moves
  largestLeap: number; // the widest move, in semitones
  maxMoveCost: number; // the costliest single move
  rhythmCost: number;
  chromaticNotes: number; // notes altered beyond the mode's scale (solfegeChromaticCount)
  mode: SolfegeMode;
  level: LearnLevel; // the level these give, before any override on the phrase
}

// A note as a move's end: a plain degree of the major scale, or a note that may be altered
type SolfegeMoveEnd = number | Pick<SolfegeNote, 'degree' | 'alteration'>;

function asNote(end: SolfegeMoveEnd): Pick<SolfegeNote, 'degree' | 'alteration'> {
  return typeof end === 'number' ? { degree: end } : end;
}

// What the move between two notes costs (see solfegeDifficultyTunables)
export function solfegeMoveCost(from: SolfegeMoveEnd, to: SolfegeMoveEnd): number {
  const [fromNote, toNote] = [asNote(from), asNote(to)];
  const semitones = Math.abs(solfegeNoteSemitones(toNote) - solfegeNoteSemitones(fromNote));
  const syllables = [solfegeNoteSyllable(fromNote), solfegeNoteSyllable(toNote)];
  if (semitones <= tunables.stepMaxSemitones) {
    return 0;
  }
  if (semitones <= tunables.thirdMaxSemitones) {
    const inHomeChord = syllables.every((syllable) => tunables.homeChord.includes(syllable));
    return inHomeChord ? tunables.homeChordThirdCost : tunables.thirdCost;
  }
  if (semitones <= tunables.fifthMaxSemitones) {
    const doSol = syllables.includes('do') && syllables.includes('sol');
    return doSol ? tunables.doSolCost : tunables.fourthOrFifthCost;
  }
  return tunables.wideLeapCost;
}

function rhythmCost(beats: number[]): number {
  let cost = 0;
  if (beats.includes(tunables.eighthBeats)) {
    cost += tunables.eighthCost;
  }
  if (beats.some((length) => tunables.sixteenthBeats.includes(length))) {
    cost += tunables.sixteenthCost;
  }
  const short = beats.filter((length) => length < tunables.shortNoteBeats).length;
  if (short > tunables.mostlyShortShare * beats.length) {
    cost += tunables.mostlyShortCost;
  }
  if (new Set(beats).size > tunables.manyNoteValues) {
    cost += tunables.manyNoteValuesCost;
  }
  return cost;
}

// How hard a phrase is, from its notes, beats and mode alone
export function solfegePhraseDifficulty(phrase: Pick<SolfegePhrase, 'notes' | 'mode'>): SolfegePhraseDifficulty {
  const { notes } = phrase;
  const moves = notes.slice(1).map((note, index) => ({
    cost: solfegeMoveCost(notes[index], note),
    semitones: Math.abs(solfegeNoteSemitones(note) - solfegeNoteSemitones(notes[index])),
  }));
  const totalCost = moves.reduce((total, move) => total + move.cost, 0);
  const leapLoad = moves.length === 0 ? 0 : totalCost / moves.length;
  const largestLeap = Math.max(0, ...moves.map((move) => move.semitones));
  const maxMoveCost = Math.max(0, ...moves.map((move) => move.cost));
  const rhythm = rhythmCost(notes.map((note) => note.beats));
  const difficulty = {
    leapLoad,
    largestLeap,
    maxMoveCost,
    rhythmCost: rhythm,
    chromaticNotes: solfegeChromaticCount(phrase),
    mode: solfegePhraseMode(phrase),
  };
  return { ...difficulty, level: levelOf(difficulty) };
}

const levelOrder = [LearnLevel.Easy, LearnLevel.Medium, LearnLevel.Hard];

// The level the jumps and rhythm give, raised to minorOrChromaticLevel for a minor or chromatic
// phrase
function levelOf(difficulty: Omit<SolfegePhraseDifficulty, 'level'>): LearnLevel {
  const level = movesAndRhythmLevel(difficulty);
  if (difficulty.mode === 'major' && difficulty.chromaticNotes === 0) {
    return level;
  }
  const floor = tunables.minorOrChromaticLevel;
  return levelOrder.indexOf(level) < levelOrder.indexOf(floor) ? floor : level;
}

function movesAndRhythmLevel({
  leapLoad,
  largestLeap,
  maxMoveCost,
  rhythmCost,
}: Omit<SolfegePhraseDifficulty, 'level'>): LearnLevel {
  if (
    largestLeap >= tunables.hardLargestLeap ||
    leapLoad >= tunables.hardLeapLoad ||
    (rhythmCost >= tunables.hardRhythmCost && maxMoveCost >= tunables.hardRhythmWithMoveCost)
  ) {
    return LearnLevel.Hard;
  }
  if (
    maxMoveCost >= tunables.mediumMoveCost ||
    leapLoad >= tunables.mediumLeapLoad ||
    rhythmCost >= tunables.mediumRhythmCost
  ) {
    return LearnLevel.Medium;
  }
  return LearnLevel.Easy;
}

// The level a phrase is served at: its override where it has one, else its difficulty's
export function solfegePhraseLevel(phrase: SolfegePhrase): LearnLevel {
  return phrase.level ?? solfegePhraseDifficulty(phrase).level;
}

// ---- The phrases ----------------------------------------------------------------------------

// What a phrase of the list may say besides its notes: which phrase of the hymn it is when it is
// not the opening, as the label on screen and as its kind, its mode when it is minor, and a level
// override (see SolfegePhrase.level)
interface SolfegePhraseOptions {
  label?: string;
  kind?: SolfegePhraseKind;
  mode?: SolfegeMode;
  level?: LearnLevel;
}

// A note as the list writes it: a degree of the phrase's own scale as a number, or a string of the
// degree and + or - for that note raised or lowered a semitone. So a major phrase writes fi as
// '3+' and te as '6-', and a minor phrase writes its own me, le and te as plain 2, 5 and 6, and a
// raised sixth or seventh, la or ti, as '5+' or '6+'. A plain number is always the mode's scale and
// a sign always a deliberate change from it, so the list can never say la in a minor tune by
// forgetting to lower it.
export type SolfegeWrittenDegree = number | `${number}${'+' | '-'}`;

const writtenAlteration = /^(-?\d+)([+-])$/;

// The degree a note is written as, and how far the writing raises or lowers it from the mode's scale
function readWritten(written: SolfegeWrittenDegree): { degree: number; change: number } {
  if (typeof written === 'number') {
    return { degree: written, change: 0 };
  }
  const match = writtenAlteration.exec(written);
  if (!match) {
    throw new Error(`${written} is not a degree, raised (+) or lowered (-)`);
  }
  return { degree: Number(match[1]), change: match[2] === '+' ? 1 : -1 };
}

// A written degree as a note's degree and its alteration from the major scale, which is how a
// note holds it whatever the mode. A note of the major scale carries no alteration.
export function solfegeWrittenNote(
  written: SolfegeWrittenDegree,
  mode: SolfegeMode = 'major',
): Pick<SolfegeNote, 'degree' | 'alteration'> {
  const { degree, change } = readWritten(written);
  const alteration = solfegeScaleAlteration(mode, degree) + change;
  // An alteration of two semitones is kept as written, for solfegePhraseProblem to reject
  return alteration === 0 ? { degree } : { degree, alteration: alteration as SolfegeAlteration };
}

// What would make a phrase draw or sound wrongly, or undefined when there is nothing: a length the
// staff cannot write, a degree that is not whole, an alteration other than a semitone either way,
// a note the mode's scale would have to alter twice to reach (li in minor, where la is le raised),
// or a note with no syllable (mi or ti raised, do or fa lowered). deckPhrase checks every phrase of
// the list with it as the module loads.
export function solfegePhraseProblem(phrase: SolfegePhrase): string | undefined {
  const mode = solfegePhraseMode(phrase);
  for (const [index, note] of phrase.notes.entries()) {
    const where = `${phrase.title}, note ${index + 1}`;
    if (!solfegeNoteValues.some((value) => value.beats === note.beats)) {
      return `${where}: no note value lasts ${note.beats} beats`;
    }
    if (!Number.isInteger(note.degree)) {
      return `${where}: degree ${note.degree} is not a whole degree`;
    }
    const alteration: number = note.alteration ?? 0;
    if (![-1, 0, 1].includes(alteration)) {
      return `${where}: an alteration of ${alteration}, more than a semitone`;
    }
    if (Math.abs(alteration - solfegeScaleAlteration(mode, note.degree)) > 1) {
      return `${where}: two semitones from the ${mode} scale's note`;
    }
    try {
      solfegeNoteSyllable(note);
    } catch {
      return `${where}: degree ${note.degree} altered by ${alteration} has no syllable`;
    }
  }
  return undefined;
}

// A phrase of the list, checked as the module loads: a mistyped phrase fails loudly at once rather
// than drawing wrongly for a singer, whatever its level
function deckPhrase(
  title: string,
  tune: string,
  beatsPerBar: number,
  bpm: number,
  degrees: SolfegeWrittenDegree[],
  beats: number[],
  { label = 'Opening phrase', kind = 'opening', mode = 'major', level }: SolfegePhraseOptions = {},
): SolfegePhrase {
  if (degrees.length !== beats.length) {
    throw new Error(`${title}: ${degrees.length} degrees but ${beats.length} durations`);
  }
  const notes = degrees.map((written, index) => ({ ...solfegeWrittenNote(written, mode), beats: beats[index] }));
  const phrase: SolfegePhrase = {
    title,
    label,
    kind,
    tune,
    beatsPerBar,
    bpm,
    notes,
    ...(mode === 'minor' ? { mode } : {}),
    ...(level ? { level } : {}),
  };
  const problem = solfegePhraseProblem(phrase);
  if (problem) {
    throw new Error(problem);
  }
  return phrase;
}

// Every phrase of every level: each level's deck is the phrases of its level in this order, but for
// those too long for the staff yet (solfegeMaxPhraseNotes), and a session moves through each kind of
// phrase of a deck in that order until a later kind joins (pickSolfegePhrases). The fifteen openings
// of the product owner's first list come first, in their order, so a singer's first sessions are
// what they were; then the phrases added after them, Easy, Medium and Hard in turn, each level's
// shortlist, then the rest of the researched list, then its minor and chromatic phrases, each run
// the most recognised first. A hymn
// may have several phrases here, told apart on screen by their labels; a hymn sung to two tunes
// says "(second tune)" in the labels of the second's phrases. A phrase that is not its hymn's
// opening says so twice in its options: its label, the words on screen, and its kind, 'closing' for
// a last phrase or the start of a refrain and 'middle' for any other line (a refrain's later lines,
// and the end of a verse that a refrain follows, among them), which decides when sessions bring it
// in. A note is written as its degree of the phrase's scale, with + or - when it is raised or
// lowered from it (SolfegeWrittenDegree): '3+' is fi in a major phrase; a minor phrase says
// `mode: 'minor'` in its options and writes me, le and te as plain 2, 5 and 6. Adding a phrase is
// one more entry here and nothing else; its notes put it in the right deck by themselves
// (solfegePhraseDifficulty):
// - each move to the next note costs by its size: a repeat or a step nothing, a third 1 (half
//   between do, mi and sol), a fourth or a fifth 2 (do to sol 1), a sixth or wider 3;
// - the rhythm costs for eighths, more for sixteenths and dotted eighths, and more again when most
//   notes are short or the phrase uses more than three lengths;
// - a sixth or wider, frequent jumps, or a busy rhythm together with a fourth or a fifth other than
//   do to sol make a phrase Hard; any such fourth or fifth, fairly frequent jumps or a rhythm past
//   quarters and eighths make it Medium; the rest are Easy;
// - a minor phrase, or one with a chromatic note, is Medium at least.
// A phrase the measure puts at the wrong level for a reason the notes cannot show (a hymn everyone
// knows) takes `level` in its options, with a comment beside it saying why.
//
// Every phrase was extracted from the hymnals repo (Cantus Christi 2020, Treasury) from the hymns on
// singyourpart.com/try-it-out: the first fifteen are the product owner's list of 2026-10-02 on
// issue #3722, the rest researched phrases checked note by note against the hymnals' soprano lines
// (issue #3772), each with its source and words beside it for maintainers (the app never shows a
// lyric). Most are major and diatonic, but the list holds minor and chromatic phrases too (the
// product owner, 2026-10-03): four of I Know That My Redeemer Lives to SHOUT ON, in do-based minor,
// and nine lines of eight hymns with one raised note each (fi, or si in Love Divine). Singers meet
// their syllables after the major scale's (issue #3785), so the scheme puts every one of them at
// Medium or above. Most tunes are public domain; How Great Thou Art, Great Is Thy Faithfulness and
// the 2014 tune of I Know That My Redeemer Lives may not be, and the product owner decided that a
// short phrase of a melody is no copyright concern. A phrase of many or short notes may need the
// staff's smaller syllables or a staff drawn smaller (solfege_staff_geometry.ts); the guards over
// every phrase the decks serve say which, and fail for a phrase the staff cannot draw at all. A
// phrase of more than solfegeMaxPhraseNotes notes may be added here, and waits out of every deck
// until the staff scrolls sideways (issue #3782).
export const solfegeAllPhrases: SolfegePhrase[] = [
  deckPhrase('Amazing Grace', 'NEW BRITAIN', 3, 76, [-3, 0, 2, 0, 2, 1, 0, -2, -3], [1, 2, 0.5, 0.5, 2, 1, 2, 1, 2]),
  deckPhrase('The Doxology', 'OLD HUNDREDTH', 4, 72, [0, 0, -1, -2, -3, 0, 1, 2], [1, 1, 1, 1, 1, 1, 1, 2]),
  deckPhrase('Holy, Holy, Holy', 'NICAEA', 4, 96, [0, 0, 2, 2, 4, 4, 5, 5, 5, 4, 2], [1, 1, 1, 1, 2, 2, 2, 1, 1, 2, 2]),
  deckPhrase('Joy to the World', 'ANTIOCH', 4, 66, [7, 6, 5, 4, 3, 2, 1, 0], [1, 0.75, 0.25, 1.5, 0.5, 1, 1, 2]),
  deckPhrase(
    'Be Thou My Vision',
    'SLANE',
    3,
    84,
    [0, 0, 1, 0, -2, -3, -2, 0, 0, 1, 2],
    [1, 1, 0.5, 0.5, 1, 1, 1, 1, 1, 1, 3],
  ),
  deckPhrase(
    'Come, Thou Fount of Every Blessing',
    'NETTLETON',
    3,
    72,
    [2, 1, 0, 0, 2, 4, 1, 1],
    [0.5, 0.5, 1, 1, 0.5, 0.5, 1, 2],
  ),
  deckPhrase(
    'A Mighty Fortress Is Our God',
    'EIN FESTE BURG',
    4,
    76,
    [0, 0, 0, -3, -2, -1, 0, -1, -2, -3],
    [1, 1, 1, 0.5, 0.5, 1, 0.5, 0.5, 1, 2],
  ),
  deckPhrase(
    'Oh, for a Thousand Tongues to Sing',
    'AZMON',
    3,
    80,
    [-3, 0, 0, 1, 1, 2, 1, 0],
    [1, 0.5, 0.5, 1, 1, 0.5, 0.5, 2],
  ),
  deckPhrase('Abide With Me', 'EVENTIDE', 4, 92, [2, 2, 1, 0, 4, 5, 4, 4, 3, 2], [2, 1, 1, 2, 2, 1, 1, 1, 1, 3]),
  deckPhrase(
    'It Is Well With My Soul',
    'VILLE DU HAVRE',
    4,
    88,
    [4, 4, 3, 2, 2, 1, 2, 3, 5, 4, 3, 2],
    [1, 2, 1, 1, 2, 1, 1, 1, 1, 1, 1, 3],
  ),
  deckPhrase(
    "All Hail the Power of Jesus' Name",
    'CORONATION',
    4,
    92,
    [-3, 0, 0, 2, 2, 1, 0, 1],
    [1, 1, 1, 1, 1, 1, 1, 2],
  ),
  deckPhrase('Crown Him with Many Crowns', 'DIADEMATA', 4, 88, [0, 0, 0, 2, 2, 5], [1, 0.5, 0.5, 1, 1, 3]),
  deckPhrase('Rejoice, the Lord Is King', 'DARWALL', 4, 100, [0, 2, 0, 4, 2, 7], [1, 1, 1, 1, 1, 3]),
  deckPhrase(
    'The King of Love My Shepherd Is',
    'ST. COLUMBA',
    3,
    84,
    [0, 1, 2, 3, 4, 3, 4, 2, 1, 0],
    [0.5, 0.5, 2, 1, 2, 0.5, 0.5, 2, 1, 2],
  ),
  deckPhrase(
    "Jesus Shall Reign Where'er the Sun",
    'DUKE STREET',
    4,
    84,
    [0, 2, 3, 4, 5, 6, 7, 6, 5, 4],
    [1, 0.5, 0.5, 1, 0.5, 0.5, 1, 0.5, 0.5, 2],
  ),
  // The phrases added for issue #3772: later lines, refrains and last phrases of these hymns, and
  // hymns not here before. The Easy ones, which Easy sessions bring in after the openings as the
  // singer qualifies at the level:
  // Cantus Christi 2020 734: "Praise Father, Son, and Holy Ghost."
  deckPhrase('The Doxology', 'OLD HUNDREDTH', 4, 72, [4, 2, 0, 1, 3, 2, 1, 0], [1, 1, 1, 1, 1, 1, 1, 3], {
    label: 'Last phrase',
    kind: 'closing',
  }),
  // Cantus Christi 2020 566: "Let earth receive her King;"
  deckPhrase('Joy to the World', 'ANTIOCH', 4, 66, [-3, -2, -2, -1, -1, 0], [0.5, 1.5, 0.5, 1.5, 0.5, 3], {
    label: 'Second line',
    kind: 'middle',
  }),
  // Cantus Christi 2020 373: "Waking or sleeping, Thy presence my light."
  deckPhrase(
    'Be Thou My Vision',
    'SLANE',
    3,
    84,
    [0, 2, 4, 5, 4, 2, 0, 2, 1, 0, 0, 0],
    [1, 1, 1, 0.5, 0.5, 1, 0.5, 0.5, 1, 1, 1, 3],
    { label: 'Last phrase', kind: 'closing' },
  ),
  // Cantus Christi 2020 408: "On earth is not his equal." (same notes as line 2, "A bulwark never failing")
  deckPhrase(
    'A Mighty Fortress Is Our God',
    'EIN FESTE BURG',
    4,
    76,
    [7, 6, 5, 4, 5, 4, 3, 2, 1, 0],
    [1, 1, 1, 1, 0.5, 0.5, 0.5, 0.5, 1, 2],
    { label: 'Last phrase', kind: 'closing' },
  ),
  // Cantus Christi 2020 537: "Rejoice, again I say, rejoice!"
  deckPhrase('Rejoice, the Lord Is King', 'DARWALL', 4, 100, [-3, -2, -1, 0, 1, 0, -1, 0], [1, 1, 1, 1, 1, 2, 2, 3], {
    label: 'Last phrase',
    kind: 'closing',
  }),
  // Cantus Christi 2020 301: "Praise Him in glad adoration."
  deckPhrase(
    'Praise to the Lord, the Almighty',
    'LOBE DEN HERREN',
    3,
    88,
    [-3, -2, -1, 0, 1, 2, 1, 0],
    [1, 1, 1, 1, 1, 1, 3, 3],
    { label: 'Last phrase', kind: 'closing' },
  ),
  // Then the rest of the researched list, which the product owner took whole for its variety
  // (2026-10-03), the most recognised first:
  // Cantus Christi 2020 461: "Was blind, but now I see."
  deckPhrase('Amazing Grace', 'NEW BRITAIN', 3, 76, [-3, 0, 2, 0, 2, 1, 0], [1, 2, 0.5, 0.5, 2, 1, 2], {
    label: 'Last phrase',
    kind: 'closing',
  }),
  // Cantus Christi 2020 734: "Praise Him above, ye heav'nly host;"
  deckPhrase('The Doxology', 'OLD HUNDREDTH', 4, 72, [0, 1, 2, 1, 0, -2, -1, 0], [1, 1, 1, 1, 1, 1, 1, 3], {
    label: 'Third line',
    kind: 'middle',
  }),
  // Treasury of Psalms and Hymns 1035: "Morning by morning new mercies I see;"
  deckPhrase(
    'Great Is Thy Faithfulness',
    'FAITHFULNESS',
    3,
    66,
    [-3, -2, -1, 0, -3, -2, -1, 0, -2, -3],
    [1, 1, 1, 1, 1, 1, 1, 1, 1, 3],
    { label: 'Refrain (second line)', kind: 'middle' },
  ),
  // Cantus Christi 2020 373: "Naught be all else to me, save that Thou art"
  deckPhrase('Be Thou My Vision', 'SLANE', 3, 84, [1, 1, 1, 1, 2, 4, 5, 4, 2, 4], [1, 1, 1, 1, 1, 1, 1, 1, 1, 3], {
    label: 'Second line',
    kind: 'middle',
  }),
  // Treasury of Psalms and Hymns 1056: "When other helpers fail and comforts flee,"
  deckPhrase('Abide With Me', 'EVENTIDE', 4, 92, [2, 2, 1, 0, 4, 4, 3, 3, 2, 1], [2, 1, 1, 2, 2, 1, 1, 1, 1, 3], {
    label: 'Third line',
    kind: 'middle',
  }),
  // Cantus Christi 2020 373: "Thou my best thought, by day or by night,"
  deckPhrase(
    'Be Thou My Vision',
    'SLANE',
    3,
    84,
    [-2, -2, -1, 0, -1, -2, -3, -5, -3, -7, -8, -9, -10],
    [1, 0.5, 0.5, 0.5, 0.5, 1, 1, 1, 1, 1, 1, 2, 2],
    { label: 'Third line', kind: 'middle' },
  ),
  // Cantus Christi 2020 323: "My great Redeemer's praise,"
  deckPhrase('Oh, for a Thousand Tongues to Sing', 'AZMON', 3, 80, [1, 2, 2, 3, 2, 1], [1, 0.5, 0.5, 1, 1, 2], {
    label: 'Second line',
    kind: 'middle',
  }),
  // Cantus Christi 2020 323: "The glories of my God and King,"
  deckPhrase(
    'Oh, for a Thousand Tongues to Sing',
    'AZMON',
    3,
    80,
    [4, 4, 2, 2, 0, 0, -2, -2],
    [1, 0.5, 0.5, 1, 1, 0.5, 0.5, 2],
    { label: 'Third line', kind: 'middle' },
  ),
  // Cantus Christi 2020 335: "Bring forth the royal diadem,"
  deckPhrase(
    "All Hail the Power of Jesus' Name",
    'CORONATION',
    4,
    92,
    [1, 2, 1, 0, 2, 4, 3, 2, 1, 2],
    [1, 1, 1, 1, 1, 0.5, 0.5, 0.5, 0.5, 2],
    { label: 'Third line', kind: 'middle' },
  ),
  // Cantus Christi 2020 378: "Joy of heav'n, to earth come down;"
  deckPhrase(
    'Love Divine, All Loves Excelling',
    'BEECHER',
    4,
    84,
    [-1, -2, 0, -2, -3, -4, -3, -5],
    [1, 1, 1, 1, 1, 0.5, 0.5, 2],
    { label: 'Second line', kind: 'middle' },
  ),
  // Cantus Christi 2020 378: "Pure, unbounded love Thou art;"
  deckPhrase(
    'Love Divine, All Loves Excelling',
    'BEECHER',
    4,
    84,
    [2, 2, 3, 2, 1, 1, 2, 1],
    [1, 1, 1, 1, 1, 0.5, 0.5, 2],
    { label: 'Sixth line', kind: 'middle' },
  ),
  // Cantus Christi 2020 44: "And He is mine for ever."
  deckPhrase('The King of Love My Shepherd Is', 'ST. COLUMBA', 3, 84, [2, 4, 3, 2, 1, 0, 0], [1, 2, 1, 2, 1, 3, 2], {
    label: 'Last phrase',
    kind: 'closing',
  }),
  // Cantus Christi 2020 44: "I nothing lack if I am His"
  deckPhrase(
    'The King of Love My Shepherd Is',
    'ST. COLUMBA',
    3,
    84,
    [4, 7, 5, 4, 3, 2, 1, 0],
    [1, 2, 1, 2, 1, 2, 1, 2],
    { label: 'Third line', kind: 'middle' },
  ),
  // Cantus Christi 2020 137: "Does his successive journeys run;"
  deckPhrase(
    "Jesus Shall Reign Where'er the Sun",
    'DUKE STREET',
    4,
    84,
    [4, 4, 4, 5, 4, 3, 2, 1],
    [1, 0.5, 0.5, 1, 1, 1, 1, 2],
    { label: 'Second line', kind: 'middle' },
  ),
  // Cantus Christi 2020 436: "I know that my Redeemer lives"
  // Gregory D. Wilbur's 2014 tune, which the hymnal names no tune for, hence "(second tune)" in its
  // labels: the hymn's first tune, SHOUT ON (435), is minor, and its phrases are among the Medium
  // ones below.
  deckPhrase(
    'I Know That My Redeemer Lives',
    'WILBUR',
    4,
    100,
    [-3, -2, -3, 0, 1, 0, 2, 1, 0, -2, -3],
    [2, 1, 1, 2, 1, 1, 2, 1, 1, 2, 2],
    { label: 'Opening phrase (second tune)', kind: 'opening' },
  ),
  // Cantus Christi 2020 436: "And with His life my sin forgives!"
  deckPhrase(
    'I Know That My Redeemer Lives',
    'WILBUR',
    4,
    100,
    [-3, -2, 0, 1, 0, 1, 2, 1, 0],
    [2, 1, 1, 2, 1, 1, 2, 2, 3],
    { label: 'Second line (second tune)', kind: 'middle' },
  ),
  // Cantus Christi 2020 436: "And patient be, though come what may."
  deckPhrase(
    'I Know That My Redeemer Lives',
    'WILBUR',
    4,
    100,
    [-3, -2, -3, 0, 1, 0, 2, 1, 0],
    [2, 1, 1, 2, 1, 1, 2, 2, 3],
    { label: 'Last phrase (second tune)', kind: 'closing' },
  ),
  // The Medium ones:
  // Cantus Christi 2020 461: "I once was lost, but now am found;"
  deckPhrase(
    'Amazing Grace',
    'NEW BRITAIN',
    3,
    76,
    [2, 4, 2, 4, 2, 0, -3, -2, 0, 0, -2, -3],
    [1, 1.5, 0.5, 0.5, 0.5, 2, 1, 1.5, 0.5, 0.5, 0.5, 2],
    { label: 'Third line', kind: 'middle' },
  ),
  // Treasury of Psalms and Hymns 717: "It is well, it is well with my soul." (end of the refrain)
  deckPhrase(
    'It Is Well With My Soul',
    'VILLE DU HAVRE',
    4,
    88,
    [-5, -3, -2, -2, 0, 0, -1, 0, 0],
    [1, 1, 2, 1, 1, 2, 1.5, 0.5, 3],
    { label: 'Last phrase', kind: 'closing' },
  ),
  // Cantus Christi 2020 566: "And heav'n and nature sing,"
  deckPhrase('Joy to the World', 'ANTIOCH', 4, 66, [2, 2, 2, 2, 2, 3, 4], [0.5, 0.5, 0.5, 0.5, 0.25, 0.25, 2], {
    label: 'Fourth line',
    kind: 'middle',
  }),
  // Cantus Christi 2020 317: "God in three Persons, blessed Trinity!"
  deckPhrase('Holy, Holy, Holy', 'NICAEA', 4, 96, [7, 4, 4, 5, 2, 3, 1, 1, 0, 0], [2, 1, 1, 2, 2, 1, 1, 1.5, 0.5, 3], {
    label: 'Last phrase',
    kind: 'closing',
  }),
  // Cantus Christi 2020 301: "Praise to the Lord, the Almighty, the King of creation!"
  deckPhrase(
    'Praise to the Lord, the Almighty',
    'LOBE DEN HERREN',
    3,
    88,
    [0, 0, 4, 2, 1, 0, -1, -2, -3, -2, -1, 0, 1, 0],
    [1, 1, 1, 1.5, 0.5, 1, 1, 1, 1, 1, 1, 1, 3, 3],
  ),
  // Cantus Christi 2020 414: "Teach me some melodious sonnet,"
  deckPhrase(
    'Come, Thou Fount of Every Blessing',
    'NETTLETON',
    3,
    72,
    [-3, -2, -1, 0, -1, -2, -3, -2, -3, -5],
    [0.5, 0.25, 0.25, 1, 1, 0.5, 0.5, 0.5, 0.5, 2],
    { label: 'Fifth line', kind: 'middle' },
  ),
  // Cantus Christi 2020 335: "And crown Him Lord of all."
  deckPhrase(
    "All Hail the Power of Jesus' Name",
    'CORONATION',
    4,
    92,
    [0, 4, 3, 2, 3, 1, 1, 0],
    [1, 2, 2, 1.5, 0.5, 1, 1, 3],
    { label: 'Last phrase', kind: 'closing' },
  ),
  // Treasury of Psalms and Hymns 1056: "Help of the helpless, O abide with me."
  deckPhrase('Abide With Me', 'EVENTIDE', 4, 92, [1, 2, 3, 2, 1, 0, 3, 2, 1, 0], [2, 1, 1, 1, 1, 1, 1, 2, 2, 3], {
    label: 'Last phrase',
    kind: 'closing',
  }),
  // Cantus Christi 2020 447: "That Thou, my God, shouldst die for me?" (the repeat that ends the hymn)
  deckPhrase(
    'And Can It Be That I Should Gain',
    'SAGINA',
    4,
    66,
    [2, 5, 5, 4, 3, 1, 0, -1, 0],
    [0.5, 1, 1, 1, 0.5, 0.5, 1, 1, 2],
    {
      label: 'Last phrase',
      kind: 'closing',
    },
  ),
  // Then the rest of the researched list, which the product owner took whole for its variety
  // (2026-10-03), the most recognised first:
  // Cantus Christi 2020 734: "Praise Him, all creatures here below;"
  deckPhrase('The Doxology', 'OLD HUNDREDTH', 4, 72, [2, 2, 2, 1, 0, 3, 2, 1], [1, 1, 1, 1, 1, 1, 1, 3], {
    label: 'Second line',
    kind: 'middle',
  }),
  // Cantus Christi 2020 461: "That saved a wretch like me;"
  deckPhrase('Amazing Grace', 'NEW BRITAIN', 3, 76, [-3, 0, 2, 0, 2, 1, 4], [1, 2, 0.5, 0.5, 2, 1, 2], {
    label: 'Second line',
    kind: 'middle',
  }),
  // Treasury of Psalms and Hymns 552: "Then sings my soul, my Savior God, to Thee:"
  deckPhrase(
    'How Great Thou Art',
    'HOW GREAT THOU ART',
    4,
    84,
    [-3, -3, 0, 2, 1, 0, -1, 0, -2, -3],
    [0.5, 0.5, 0.5, 1.5, 0.5, 0.5, 0.5, 0.5, 0.5, 3],
    { label: 'Refrain', kind: 'closing' },
  ),
  // Treasury of Psalms and Hymns 552: "O Lord my God, when I in awesome wonder,"
  deckPhrase(
    'How Great Thou Art',
    'HOW GREAT THOU ART',
    4,
    84,
    [4, 4, 4, 2, 4, 4, 4, 5, 5, 3, 5],
    [0.5, 0.5, 0.5, 1.5, 0.5, 0.5, 0.5, 0.5, 0.5, 1, 2],
  ),
  // Treasury of Psalms and Hymns 1035: "Great is Thy faithfulness, O God my Father,"
  deckPhrase(
    'Great Is Thy Faithfulness',
    'FAITHFULNESS',
    3,
    66,
    [2, 2, 2, 2, 1, 1, 3, 3, 3, 3, 2],
    [1, 1, 1, 1.5, 0.5, 1, 1, 1, 1, 1, 2],
  ),
  // Treasury of Psalms and Hymns 1035: "Great is Thy faithfulness! Great is Thy faithfulness!"
  deckPhrase(
    'Great Is Thy Faithfulness',
    'FAITHFULNESS',
    3,
    66,
    [4, 4, 1, 3, 2, 2, 5, 5, 2, 4, 3, 3],
    [1, 1, 1, 0.75, 0.25, 2, 1, 1, 1, 0.75, 0.25, 2],
    { label: 'Refrain', kind: 'closing' },
  ),
  // Treasury of Psalms and Hymns 717: "It is well, it is well with my soul."
  // The verse's last line, before the refrain: its words change with each verse.
  deckPhrase(
    'It Is Well With My Soul',
    'VILLE DU HAVRE',
    4,
    88,
    [-2, -3, 0, 0, 0, 0, -1, 0, 0],
    [1, 1, 2, 1, 1, 2, 1.5, 0.5, 2],
    { label: 'End of the verse', kind: 'middle' },
  ),
  // Cantus Christi 2020 317: "Early in the morning our song shall rise to Thee."
  deckPhrase(
    'Holy, Holy, Holy',
    'NICAEA',
    4,
    96,
    [-3, -3, -3, -3, 0, -1, -3, -6, -3, -2, -3, -3],
    [1.5, 0.5, 1, 1, 2, 1, 1, 1, 1, 1.5, 0.5, 3],
    { label: 'Second line', kind: 'middle' },
  ),
  // Cantus Christi 2020 447: "Amazing love! How can it be" (the repeat)
  // The repeat of the fifth line, the seventh line sung, to new notes.
  deckPhrase(
    'And Can It Be That I Should Gain',
    'SAGINA',
    4,
    66,
    [0, 2, 2, 1, 1, 3, 3, 2],
    [0.5, 1, 1, 1.5, 0.5, 1, 1, 2],
    { label: 'Seventh line', kind: 'middle' },
  ),
  // Treasury of Psalms and Hymns 1035: "Great is Thy faithfulness, Lord, unto me!"
  deckPhrase(
    'Great Is Thy Faithfulness',
    'FAITHFULNESS',
    3,
    66,
    [-2, -1, 0, 0, -3, -3, -5, -4, -8, -7],
    [1, 1, 1, 1.5, 0.5, 1, 1, 1, 1, 3],
    { label: 'Last phrase', kind: 'closing' },
  ),
  // Treasury of Psalms and Hymns 552: "Consider all the worlds Thy hands have made,"
  deckPhrase(
    'How Great Thou Art',
    'HOW GREAT THOU ART',
    4,
    84,
    [5, 5, 5, 4, 2, 4, 4, 3, 3, 2],
    [0.5, 0.5, 0.5, 1.5, 0.5, 0.5, 0.5, 0.5, 0.5, 3],
    { label: 'Second line', kind: 'middle' },
  ),
  // Cantus Christi 2020 414: "Mount of Thy redeeming love." (same notes as line 2, "Tune my heart to
  // sing Thy grace")
  deckPhrase(
    'Come, Thou Fount of Every Blessing',
    'NETTLETON',
    3,
    72,
    [2, 4, 5, 4, 2, 1, 0],
    [0.5, 0.5, 1, 1, 0.5, 0.5, 2],
    { label: 'Last phrase', kind: 'closing' },
  ),
  // Cantus Christi 2020 531: "And hail Him as thy matchless King through all eternity."
  deckPhrase(
    'Crown Him with Many Crowns',
    'DIADEMATA',
    4,
    88,
    [6, 7, 6, 5, 4, 3, 1, 2, 4, 3, 2, 1, 1, 0],
    [1, 1.5, 0.5, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 3],
    { label: 'Last phrase', kind: 'closing' },
  ),
  // Cantus Christi 2020 392: "To you who for refuge to Jesus hath fled?" (same notes as line 2, "Is
  // laid for your faith in His excellent Word")
  deckPhrase(
    'How Firm a Foundation',
    'FOUNDATION',
    4,
    64,
    [-3, -2, 0, -2, 0, -3, 0, 0, 2, 1, 1, 0],
    [0.5, 0.5, 1, 0.5, 0.5, 1, 0.5, 0.5, 1, 0.5, 0.5, 2],
    { label: 'Last phrase', kind: 'closing' },
  ),
  // Cantus Christi 2020 323: "The triumphs of His grace!"
  deckPhrase(
    'Oh, for a Thousand Tongues to Sing',
    'AZMON',
    3,
    80,
    [0, -2, -3, 0, 0, 1, 0],
    [0.5, 0.5, 0.5, 0.5, 1, 1, 2],
    { label: 'Last phrase', kind: 'closing' },
  ),
  // Cantus Christi 2020 137: "Till moons shall wax and wane no more."
  deckPhrase(
    "Jesus Shall Reign Where'er the Sun",
    'DUKE STREET',
    4,
    84,
    [4, 5, 6, 7, 3, 2, 1, 0],
    [1, 0.5, 0.5, 1.5, 0.5, 1, 1, 2],
    { label: 'Last phrase', kind: 'closing' },
  ),
  // Treasury of Psalms and Hymns 1035: "Thou changest not, Thy compassions they fail not;"
  deckPhrase(
    'Great Is Thy Faithfulness',
    'FAITHFULNESS',
    3,
    66,
    [-3, -2, -1, 0, -1, -2, -3, -4, -5, -5, -6],
    [1, 1, 1, 1.5, 0.5, 1, 1, 1, 1, 1, 2],
    { label: 'Third line', kind: 'middle' },
  ),
  // Treasury of Psalms and Hymns 1035: "As Thou hast been Thou forever wilt be."
  // The verse's last line, before the refrain.
  deckPhrase(
    'Great Is Thy Faithfulness',
    'FAITHFULNESS',
    3,
    66,
    [5, 6, 7, 7, 4, 4, 2, 2, 1, 0],
    [1, 1, 1, 1.5, 0.5, 1, 1, 1, 1, 3],
    { label: 'End of the verse', kind: 'middle' },
  ),
  // Cantus Christi 2020 414: "Sung by flaming tongues above;"
  deckPhrase(
    'Come, Thou Fount of Every Blessing',
    'NETTLETON',
    3,
    72,
    [-3, -2, -1, 0, -1, -2, -3, 0],
    [0.5, 0.25, 0.25, 1, 1, 0.5, 0.5, 2],
    { label: 'Sixth line', kind: 'middle' },
  ),
  // Treasury of Psalms and Hymns 717: "Whatever my lot, Thou hast taught me to say,"
  deckPhrase(
    'It Is Well With My Soul',
    'VILLE DU HAVRE',
    4,
    88,
    [-3, 0, 0, -1, -2, -2, -2, 1, 1, 0, -1],
    [1, 2, 1, 1, 2, 1, 1, 2, 1, 1, 2],
    { label: 'Third line', kind: 'middle' },
  ),
  // Cantus Christi 2020 301: "All ye who hear, now to His temple draw near;"
  deckPhrase(
    'Praise to the Lord, the Almighty',
    'LOBE DEN HERREN',
    3,
    88,
    [4, 4, 4, 5, 2, 3, 4, 4, 3, 2, 1],
    [1, 1, 1, 3, 1, 1, 1, 1, 1, 1, 3],
    { label: 'Third line', kind: 'middle' },
  ),
  // Cantus Christi 2020 531: "The Lamb upon His throne."
  deckPhrase('Crown Him with Many Crowns', 'DIADEMATA', 4, 88, [5, 4, 0, 3, 2, 1], [1, 1, 1, 1, 1, 3], {
    label: 'Second line',
    kind: 'middle',
  }),
  // Cantus Christi 2020 137: "His kingdom stretch from shore to shore,"
  deckPhrase(
    "Jesus Shall Reign Where'er the Sun",
    'DUKE STREET',
    4,
    84,
    [2, 2, 1, 0, 2, 4, 7, 5, 4, 3, 2, 1],
    [1, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 2],
    { label: 'Third line', kind: 'middle' },
  ),
  // Cantus Christi 2020 336: "All hail the pow'r of Jesus' name!"
  // DIADEM (James Ellor), the tune many churches sing these words to beside CORONATION.
  deckPhrase(
    "All Hail the Power of Jesus' Name",
    'DIADEM',
    3,
    66,
    [-3, 0, 1, 2, 3, 4, 0, 1, 0, -1, 0],
    [1, 2, 1, 0.75, 0.25, 1, 1, 1, 1, 1, 2],
    { label: 'Opening phrase (second tune)', kind: 'opening' },
  ),
  // Cantus Christi 2020 336: "And crown Him Lord of all!"
  deckPhrase(
    "All Hail the Power of Jesus' Name",
    'DIADEM',
    3,
    66,
    [4, 0, 1, 2, 3, 2, 1, 0],
    [1, 0.75, 0.25, 1, 1, 2, 1, 2],
    { label: 'Last phrase (second tune)', kind: 'closing' },
  ),
  // Cantus Christi 2020 436: "O Jesus, Lord, I'll hope and pray"
  deckPhrase(
    'I Know That My Redeemer Lives',
    'WILBUR',
    4,
    100,
    [2, 4, 2, 5, 4, 2, 1, 2, 1, -2, -3],
    [2, 1, 1, 2, 1, 1, 2, 1, 1, 2, 2],
    { label: 'Third line (second tune)', kind: 'middle' },
  ),
  // Cantus Christi 2020 536: "Ye choirs of new Jerusalem,"
  deckPhrase(
    'Ye Choirs of New Jerusalem',
    'NORTHFIELD',
    4,
    66,
    [0, 0, -2, -3, -3, -2, 0, 1],
    [1, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 2],
  ),
  // Then the minor and chromatic phrases (issue #3772), which the product owner wanted in the decks
  // for testing before Medium is played (2026-10-03): each line with a raised note, the most
  // recognised first, all of them Medium by the scheme for that note alone or more, then I Know That
  // My Redeemer Lives to SHOUT ON, in do-based minor.
  // Cantus Christi 2020 408: "For still our ancient foe" (Bach's rhythm, as the deck's other lines of
  // this hymn). Sol, fi, sol on "ancient foe": fi a lower neighbour.
  deckPhrase('A Mighty Fortress Is Our God', 'EIN FESTE BURG', 4, 76, [0, 4, 5, 4, '3+', 4], [1, 1, 1, 1, 1, 3], {
    label: 'Fifth line',
    kind: 'middle',
  }),
  // Treasury of Psalms and Hymns 717: "When sorrows like sea billows roll;" sol, fi, sol on "billows
  // roll", a lower neighbour.
  deckPhrase(
    'It Is Well With My Soul',
    'VILLE DU HAVRE',
    4,
    88,
    [-3, 0, -1, -2, -2, -3, '-4+', -3],
    [1, 2, 1, 1, 2, 1, 1, 3],
    { label: 'Second line', kind: 'middle' },
  ),
  // Treasury of Psalms and Hymns 1056: "The darkness deepens; Lord, with me abide." Mi, fi, sol rising
  // by step on "me abide".
  deckPhrase('Abide With Me', 'EVENTIDE', 4, 92, [2, 3, 4, 5, 4, 3, 1, 2, '3+', 4], [2, 1, 1, 2, 2, 1, 1, 1, 1, 3], {
    label: 'Second line',
    kind: 'middle',
  }),
  // Treasury of Psalms and Hymns 1035: "There is no shadow of turning with Thee;" fi on "with",
  // rising by step mi, fi, sol to the cadence.
  deckPhrase(
    'Great Is Thy Faithfulness',
    'FAITHFULNESS',
    3,
    66,
    [5, 6, 5, 4, 3, 2, 1, 2, '3+', 4],
    [1, 1, 1, 1.5, 0.5, 1, 1, 1, 1, 3],
    { label: 'Second line', kind: 'middle' },
  ),
  // Cantus Christi 2020 335: "And crown Him Lord of all." (the first time; the second ends the hymn
  // and is its last phrase above) Sol slurred to fi on "of", resolving to sol.
  deckPhrase(
    "All Hail the Power of Jesus' Name",
    'CORONATION',
    4,
    92,
    [-3, -3, -3, -2, -3, '-4+', -3],
    [1, 2, 2, 2, 1, 1, 3],
    { label: 'Fourth line', kind: 'middle' },
  ),
  // Cantus Christi 2020 531: "Hark! how the heav'nly anthem drowns" fi on "an-" in sol, fi, mi.
  deckPhrase(
    'Crown Him with Many Crowns',
    'DIADEMATA',
    4,
    88,
    [1, 2, 4, 5, 4, '3+', 2, 1, 4],
    [1, 1, 1, 1, 1, 1, 0.5, 0.5, 2],
    { label: 'Third line', kind: 'middle' },
  ),
  // Cantus Christi 2020 378: "Jesus, Thou art all compassion," si on "-pas-" after la, ti, la, then
  // a major third down to mi.
  deckPhrase(
    'Love Divine, All Loves Excelling',
    'BEECHER',
    4,
    84,
    [0, 0, 0, -1, -2, -1, -2, '-3+', -5],
    [1, 1, 1, 1, 1, 0.5, 0.5, 1, 2],
    { label: 'Fifth line', kind: 'middle' },
  ),
  // I Know That My Redeemer Lives to SHOUT ON (Cantus Christi 2020 435), the hymn's first tune in
  // the hymnal, in do-based minor with do on D (the hymnal's one flat reads as F major, but every
  // line ends on D and the last chord is D minor). Written in cut time: each written half is a beat
  // here, at 66 bpm, so its sixteenths last 227 ms and are passing notes. Its lines are labelled
  // plainly; the 2014 tune above says "(second tune)".
  // Cantus Christi 2020 435: "I know that my Redeemer lives;" (same notes as line 2, "What comfort
  // this assurance gives!")
  deckPhrase(
    'I Know That My Redeemer Lives',
    'SHOUT ON',
    4,
    66,
    [2, 3, 4, 4, 4, 2, 4, 3, 2, 0],
    [0.75, 0.25, 0.5, 0.5, 0.5, 0.25, 0.25, 0.5, 0.5, 2],
    { mode: 'minor' },
  ),
  // Cantus Christi 2020 435: "Shout on, pray on, we're gaining ground" Sol, le, te: the one phrase
  // with both le and te. The research suggested Hard for them; the scheme puts it at Medium, and
  // the scheme decides.
  deckPhrase(
    'I Know That My Redeemer Lives',
    'SHOUT ON',
    4,
    66,
    [4, 5, 6, 6, 6, 4, 2, 3, 2, 0],
    [0.75, 0.25, 0.5, 0.5, 0.5, 0.25, 0.25, 0.5, 0.5, 2],
    { label: 'Refrain', kind: 'closing', mode: 'minor' },
  ),
  // Cantus Christi 2020 435: "Glory, hallelujah!" (same notes as the verse's "Glory, hallelujah!")
  deckPhrase('I Know That My Redeemer Lives', 'SHOUT ON', 4, 66, [2, 4, 3, 2, 0, 0], [0.75, 0.25, 0.5, 0.5, 1, 2], {
    label: 'Last phrase',
    kind: 'closing',
    mode: 'minor',
  }),
  // Cantus Christi 2020 435: "Glory, hallelujah!" (the refrain's) Te rising by step to high do.
  deckPhrase('I Know That My Redeemer Lives', 'SHOUT ON', 4, 66, [-5, -4, -3, -1, 0, 0], [0.75, 0.25, 0.5, 0.5, 1, 2], {
    label: 'Refrain (second line)',
    kind: 'middle',
    mode: 'minor',
  }),
  // The refrain's third line ("The dead's alive and the lost is found", te do sol fa sol me sol fa
  // me do) is left out: four of its ten notes are sixteenths, passing notes at 66 bpm, and a phrase
  // more than a third passing notes is no fair exercise.
  // The Hard ones:
  // Cantus Christi 2020 447: "And can it be that I should gain" (written in halves at 200: each
  // written half is a beat here). Cantus Christi sets "I" as fa slurred down a minor sixth to the la
  // below; its .syp, MIDI and PDF agree. Other hymnals may print that bar differently. The product
  // owner asked for the hymn's opening to be in the decks (2026-10-03); the scheme puts it here.
  deckPhrase(
    'And Can It Be That I Should Gain',
    'SAGINA',
    4,
    66,
    [0, 0, 1, 2, 4, 3, -2, -1, 1, 0],
    [1, 0.75, 0.25, 1.5, 0.5, 0.5, 0.5, 0.5, 0.5, 2],
  ),
  // Cantus Christi 2020 566: "And heav'n, and heav'n, and nature sing."
  deckPhrase(
    'Joy to the World',
    'ANTIOCH',
    4,
    66,
    [2, 1, 0, 7, 5, 4, 3, 2, 3, 2, 1, 0],
    [0.25, 0.25, 0.5, 1, 0.5, 0.75, 0.25, 0.5, 0.5, 1, 1, 2],
    { label: 'Last phrase', kind: 'closing' },
  ),
  // Cantus Christi 2020 378: "Love divine, all loves excelling,"
  deckPhrase(
    'Love Divine, All Loves Excelling',
    'BEECHER',
    4,
    84,
    [-3, -3, -2, -3, 2, 1, 2, 1, 0],
    [1, 1, 1, 1, 1, 0.5, 0.5, 1, 2],
  ),
  // Cantus Christi 2020 392: "How firm a foundation, ye saints of the Lord," (cut time: each
  // written half is a beat here)
  deckPhrase(
    'How Firm a Foundation',
    'FOUNDATION',
    4,
    64,
    [-3, -2, 0, -2, 0, -3, 0, 0, 2, 0, 2, -3],
    [0.5, 0.5, 1, 0.5, 0.5, 1, 0.5, 0.5, 1, 0.5, 0.5, 2],
  ),
  // Cantus Christi 2020 537: "Lift up your heart, lift up your voice!"
  deckPhrase('Rejoice, the Lord Is King', 'DARWALL', 4, 100, [4, 5, 6, 7, 0, 1, 2, 3], [1, 2, 2, 3, 1, 1, 1, 2], {
    label: 'Refrain',
    kind: 'closing',
  }),
  // Cantus Christi 2020 531: "Awake, my soul, and sing"
  deckPhrase('Crown Him with Many Crowns', 'DIADEMATA', 4, 88, [4, 4, 2, 1, 0, 5], [1, 1, 1, 1, 1, 3], {
    label: 'Fifth line',
    kind: 'middle',
  }),
  // Cantus Christi 2020 378: "Enter every trembling heart."
  deckPhrase(
    'Love Divine, All Loves Excelling',
    'BEECHER',
    4,
    84,
    [-2, 3, 2, 0, -1, -2, -1, 0],
    [1, 1, 1, 1, 1, 0.5, 0.5, 2],
    { label: 'Last phrase', kind: 'closing' },
  ),
  // Then the rest of the researched list, which the product owner took whole for its variety
  // (2026-10-03), the most recognised first:
  // Cantus Christi 2020 566: "Let every heart prepare Him room,"
  // Sixteen notes: it waits out of every deck until the staff scrolls sideways (issue #3782).
  deckPhrase(
    'Joy to the World',
    'ANTIOCH',
    4,
    66,
    [0, 0, -1, -2, -3, -3, -4, -5, 0, 0, -1, -2, -3, -3, -4, -5],
    [0.5, 0.5, 0.5, 0.5, 0.5, 0.75, 0.25, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.75, 0.25, 2],
    { label: 'Third line', kind: 'middle' },
  ),
  // Cantus Christi 2020 447: "Amazing love! How can it be"
  deckPhrase(
    'And Can It Be That I Should Gain',
    'SAGINA',
    4,
    66,
    [2, 1, -3, 0, 1, 2, 3, -2, 1, 0, -1, -2, -3],
    [1, 0.5, 0.5, 1, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.75, 0.25, 2],
    { label: 'Fifth line', kind: 'middle' },
  ),
  // Cantus Christi 2020 392: "What more can He say than to you He hath said,"
  deckPhrase(
    'How Firm a Foundation',
    'FOUNDATION',
    4,
    64,
    [2, 4, 2, 4, 0, 0, 0, 2, 0, 2, -3],
    [1, 1, 0.5, 0.5, 1, 0.5, 0.5, 1, 0.5, 0.5, 2],
    { label: 'Third line', kind: 'middle' },
  ),
  // Cantus Christi 2020 536: "In strains of holy joy."
  deckPhrase(
    'Ye Choirs of New Jerusalem',
    'NORTHFIELD',
    4,
    66,
    [-3, 2, 1, 0, 4, 3, 1, 2],
    [0.5, 0.75, 0.25, 0.5, 0.5, 1, 1, 2],
    { label: 'Fourth line', kind: 'middle' },
  ),
  // Then the lines with a raised note (issue #3772) whose sixths make them Hard whatever the note,
  // the most recognised first:
  // Cantus Christi 2020 531: "Of Him who died for thee," "Awake, my soul, and sing" a step higher,
  // with fi where that line has mi, ending re up a major sixth to ti.
  deckPhrase('Crown Him with Many Crowns', 'DIADEMATA', 4, 88, [5, 5, '3+', 2, 1, 6], [1, 1, 1, 1, 1, 3], {
    label: 'Sixth line',
    kind: 'middle',
  }),
  // Cantus Christi 2020 537: "Rejoice, give thanks, and sing," ending on fi, held: the next line,
  // "And triumph evermore", resolves it.
  deckPhrase('Rejoice, the Lord Is King', 'DARWALL', 4, 100, [1, 2, 0, 5, 4, '3+'], [1, 1, 1, 1, 1, 2], {
    label: 'Third line',
    kind: 'middle',
  }),
  // And Can It Be's sixth line (Cantus Christi 2020 447, "That Thou, my God, shouldst die for me?",
  // thirteen eighths that leap as far as a sixth) is left out for now: its notes cannot be kept
  // apart across a phone's width even on the smallest staff, so it waits for the staff to scroll
  // sideways (issue #3782) like any phrase too long for it.
];

// The syllables under the phrase's notes, in order
export function phraseSyllables(phrase: SolfegePhrase): SolfegeSyllable[] {
  return phrase.notes.map(solfegeNoteSyllable);
}

// The phrase's notes in semitones from do, in order
export function phraseSemitones(phrase: SolfegePhrase): number[] {
  return phrase.notes.map(solfegeNoteSemitones);
}

// The phrase's notes once do has been chosen
export function phraseMidis(phrase: SolfegePhrase, homeMidi: number): number[] {
  return phrase.notes.map((note) => solfegeNoteMidi(homeMidi, note));
}

// ---- Levels ----------------------------------------------------------------------------------

// The levels a session can be played at, with their grading. Only Easy is built (issue #3722);
// Medium and Hard join this union and the table below when issue #3772 decides what they change.
// Their decks exist already (solfegeDeck), so a phrase can be added at any level before then.
export type SolfegeLevel = LearnLevel.Easy;

export interface SolfegeLevelParams {
  toleranceCents: number; // on pitch (green), for a note's grade and the trace's colour
  closeCents: number; // close (yellow), likewise
  octaveCounts: boolean; // a note sung an octave off counts
  // Every grading window starts and ends this much later than its note: the mic's input latency
  // plus the analysis window, which nothing measures per device yet
  voiceLagMs: number;
  // The start of each window is skipped while the voice travels to the note, and while frames
  // already in flight are still analysed against the previous note: this share of the note's
  // duration, at most settleMaxMs
  settleMaxMs: number;
  settleShare: number;
  minGradedMs: number; // a shorter note at its phrase's tempo is a passing note, never graded
  greenOnShare: number; // green when the voice was on pitch for at least this share of the window
  yellowOnShare: number; // yellow when on pitch for at least this share,
  yellowNearShare: number; // or close for at least this share
  takeTailMs: number; // the take runs this long past the last note, so the last window closes
}

export const solfegeLevelParams: Record<SolfegeLevel, SolfegeLevelParams> = {
  [LearnLevel.Easy]: {
    toleranceCents: 50,
    closeCents: 100,
    octaveCounts: true,
    voiceLagMs: 70,
    settleMaxMs: 150,
    settleShare: 0.3,
    minGradedMs: 300,
    greenOnShare: 0.5,
    yellowOnShare: 0.25,
    yellowNearShare: 0.5,
    takeTailMs: 300,
  },
};

const easyParams = solfegeLevelParams[LearnLevel.Easy];

// A phrase of more notes than this cannot be laid out across a phone's width yet: even with the
// staff's smaller syllables and a staff drawn smaller its notes would crowd into each other. Such a
// phrase may sit in solfegeAllPhrases already, but it waits out of every deck until the staff
// scrolls sideways (issue #3782); raising this then brings it in with nothing else to change.
export const solfegeMaxPhraseNotes = 14;

function fitsPhoneWidth(phrase: SolfegePhrase): boolean {
  return phrase.notes.length <= solfegeMaxPhraseNotes;
}

// The phrases of `phrases` waiting for the staff to scroll sideways: too long to serve yet
export function solfegePhrasesAwaitingScroll(phrases: readonly SolfegePhrase[] = solfegeAllPhrases): SolfegePhrase[] {
  return phrases.filter((phrase) => !fitsPhoneWidth(phrase));
}

// Every phrase a deck serves, in the order of solfegeAllPhrases: all of them but those waiting for
// the staff to scroll. The guards on drawing a phrase hold for these.
export const solfegeServedPhrases: SolfegePhrase[] = solfegeAllPhrases.filter(fitsPhoneWidth);

// A level's deck from `phrases`: those of the level short enough to draw, in their order
export function solfegeDeckOf(phrases: readonly SolfegePhrase[], level: LearnLevel): SolfegePhrase[] {
  return phrases.filter((phrase) => fitsPhoneWidth(phrase) && solfegePhraseLevel(phrase) === level);
}

// Every level's deck, Medium and Hard included though only Easy is played yet (issue #3772), drawn
// once as the module loads so a deck is the same array every time it is asked for
const decks = Object.fromEntries(
  Object.values(LearnLevel).map((level) => [level, solfegeDeckOf(solfegeAllPhrases, level)]),
) as Record<LearnLevel, SolfegePhrase[]>;

// The phrases of a level, in the order of solfegeAllPhrases
export function solfegeDeck(level: LearnLevel): SolfegePhrase[] {
  return decks[level];
}

export const solfegePhrasesPerSession = 4;

// A phrase's summary square on the session summary: green when its points reach this share of its
// graded notes, yellow when they reach the second, red below
export const solfegePhraseGreenShare = 0.8;
export const solfegePhraseYellowShare = 0.5;

// ---- Timing ----------------------------------------------------------------------------------

// Every time here is in ms from the take's start, the moment the first note should begin; the page
// adds the take's start on the audio clock.

export interface SolfegeSpan {
  startMs: number;
  endMs: number;
}

export function phraseBeatMs(phrase: SolfegePhrase): number {
  return 60000 / phrase.bpm;
}

// The count-in, one bar, ends when the take starts
export function phraseCountInMs(phrase: SolfegePhrase): number {
  return phrase.beatsPerBar * phraseBeatMs(phrase);
}

// When each note starts and ends, back to back from 0
export function phraseNoteSpans(phrase: SolfegePhrase): SolfegeSpan[] {
  const beatMs = phraseBeatMs(phrase);
  let beat = 0;
  return phrase.notes.map((note) => {
    const span = { startMs: beat * beatMs, endMs: (beat + note.beats) * beatMs };
    beat += note.beats;
    return span;
  });
}

// When the last note ends
export function phraseLengthMs(phrase: SolfegePhrase): number {
  const beats = phrase.notes.reduce((total, note) => total + note.beats, 0);
  return beats * phraseBeatMs(phrase);
}

// When the take ends and the result shows: the last note's end plus the tail
export function phraseTakeEndMs(phrase: SolfegePhrase, params: SolfegeLevelParams = easyParams): number {
  return phraseLengthMs(phrase) + params.takeTailMs;
}

// Whether each note is graded: false for a passing note too short to grade at the phrase's tempo
export function phraseGradedNotes(phrase: SolfegePhrase, params: SolfegeLevelParams = easyParams): boolean[] {
  const beatMs = phraseBeatMs(phrase);
  return phrase.notes.map((note) => note.beats * beatMs >= params.minGradedMs);
}

// How long the start of a note lasting `durationMs` is skipped while the voice travels to it (see
// SolfegeLevelParams): grading skips it, and the trace leaves it unjudged. One formula for both, so
// the line and the grades never disagree about where a note's settle ends.
export function solfegeSettleMs(durationMs: number, params: SolfegeLevelParams = easyParams): number {
  return Math.min(params.settleMaxMs, params.settleShare * durationMs);
}

// The stretch of each note the voice is graded over: shifted by the voice's lag, with the settle
// skipped at the start (see SolfegeLevelParams). A passing note has one too; it is just not used.
export function phraseGradingWindows(phrase: SolfegePhrase, params: SolfegeLevelParams = easyParams): SolfegeSpan[] {
  return phraseNoteSpans(phrase).map(({ startMs, endMs }) => {
    const settleMs = solfegeSettleMs(endMs - startMs, params);
    return { startMs: startMs + params.voiceLagMs + settleMs, endMs: endMs + params.voiceLagMs };
  });
}

// ---- Grading ---------------------------------------------------------------------------------

// A phrase's note colours, one per note: undefined for a passing note, which is never graded
export type SolfegeNoteColors = (ResultColor | undefined)[];

// A graded note's colour from the shares of its window the voice spent on pitch and close
export function solfegeNoteColor(
  onShare: number,
  nearShare: number,
  params: SolfegeLevelParams = easyParams,
): ResultColor {
  if (onShare >= params.greenOnShare) {
    return 'green';
  }
  return onShare >= params.yellowOnShare || nearShare >= params.yellowNearShare ? 'yellow' : 'red';
}

// The colour of a moment of voice by its distance from the note sounding then, for the trace and
// the dot: the same bands a note is graded by
export function solfegeCentsColor(cents: number, params: SolfegeLevelParams = easyParams): ResultColor {
  const distance = Math.abs(cents);
  if (distance <= params.toleranceCents) {
    return 'green';
  }
  return distance <= params.closeCents ? 'yellow' : 'red';
}

// ---- Octaves ---------------------------------------------------------------------------------

// When octaves count, a reading is folded into the singer's register rather than to the octave
// nearest the note (issue #3722, review). The nearest-octave fold turned every miss of more than a
// tritone into a miss the other way: a singer who stays on do as the phrase leaps up to sol is 700
// cents flat, and folded to the nearest octave read 500 cents sharp, so the hint said "a little
// lower", the dot sat above the note and the sentence called the sol sharp. So a reading within
// solfegeRegisterCents of a whole number of octaves from the note says where the singer is singing:
// that number of octaves is taken as the take's register and the reading is folded by it. Any other
// reading is ambiguous, and is folded by the register the take last established (the note's own
// octave until one is), unclamped: 700 flat stays 700 flat, for a singer in the note's own octave
// and for a man an octave down alike. The session folds every frame with this, and the trace folds
// the line's distances from the notes with the same function, so the two never disagree.
export const solfegeRegisterCents = 350;

export interface SolfegeFold {
  cents: number; // the reading's distance from the note, folded into the register
  register: number; // the register to fold the next reading by, in octaves from the note's own
}

// `cents` from the note, as the detectors read it (an octave off where they heard one); `register`
// the one the take has established so far (0 to begin with)
export function solfegeFoldCents(
  cents: number,
  register: number,
  params: SolfegeLevelParams = easyParams,
): SolfegeFold {
  if (!params.octaveCounts || !Number.isFinite(cents)) {
    return { cents, register };
  }
  const octaves = Math.round(cents / 1200) || 0; // never -0
  const residual = cents - 1200 * octaves;
  if (Math.abs(residual) <= solfegeRegisterCents) {
    return { cents: residual === 0 ? 0 : residual, register: octaves }; // never -0
  }
  const folded = cents - 1200 * register;
  return { cents: folded === 0 ? 0 : folded, register };
}

export interface SolfegeNoteCount {
  onPitch: number; // green notes
  graded: number; // every note but the passing notes
}

// "8 of 9": the green notes of the graded ones
export function solfegeNoteCount(colors: SolfegeNoteColors): SolfegeNoteCount {
  const graded = colors.filter((color) => color !== undefined);
  return { onPitch: graded.filter((color) => color === 'green').length, graded: graded.length };
}

// "8 of 9 notes", as the result screen heads a phrase
export function solfegeNoteCountText(colors: SolfegeNoteColors): string {
  const { onPitch, graded } = solfegeNoteCount(colors);
  return `${onPitch} of ${graded} notes`;
}

// One point for a green note and half for a yellow one
export function solfegePhrasePoints(colors: SolfegeNoteColors): number {
  return colors.reduce((points, color) => points + (color === 'green' ? 1 : color === 'yellow' ? 0.5 : 0), 0);
}

// The phrase's square on the session summary
export function solfegePhraseColor(colors: SolfegeNoteColors): ResultColor {
  const { graded } = solfegeNoteCount(colors);
  const share = graded === 0 ? 0 : solfegePhrasePoints(colors) / graded;
  if (share >= solfegePhraseGreenShare) {
    return 'green';
  }
  return share >= solfegePhraseYellowShare ? 'yellow' : 'red';
}

// The session's score out of learnSessionScoreMax, over the last take of each phrase: the points
// over every graded note, so a long phrase weighs more than a short one
export function solfegeSessionScore(phrases: SolfegeNoteColors[]): number {
  const graded = phrases.reduce((total, colors) => total + solfegeNoteCount(colors).graded, 0);
  if (graded === 0) {
    return 0;
  }
  const points = phrases.reduce((total, colors) => total + solfegePhrasePoints(colors), 0);
  return Math.round((learnSessionScoreMax * points) / graded);
}

// The session summary's best streak: the longest run of green notes in the session's last takes,
// in the order they were sung. A run carries on from one phrase into the next, and a passing note
// neither breaks it nor adds to it.
export function solfegeLongestGreenRun(phrases: SolfegeNoteColors[]): number {
  let longest = 0;
  let run = 0;
  for (const color of phrases.flat()) {
    if (color === undefined) {
      continue;
    }
    run = color === 'green' ? run + 1 : 0;
    longest = Math.max(longest, run);
  }
  return longest;
}

// ---- The home-note line ----------------------------------------------------------------------

export type SolfegeStartPosition = 'below' | 'on' | 'above';

// What the listen screen's home-note line needs: "This phrase starts on sol, below do." The
// position is the first note's pitch against do itself, so a phrase starting on the do above (Joy
// to the World) starts on do, above do, and one starting on a minor tune's me starts above do.
export function phraseStart(phrase: SolfegePhrase): { syllable: SolfegeSyllable; position: SolfegeStartPosition } {
  const first = phrase.notes[0];
  const semitones = solfegeNoteSemitones(first);
  const position = semitones < 0 ? 'below' : semitones > 0 ? 'above' : 'on';
  return { syllable: solfegeNoteSyllable(first), position };
}

// ---- Fitting the range -----------------------------------------------------------------------

// The phrase's lowest and highest note from do, do included, as the home-note rule takes them: by
// the notes' real semitones, so a raised or lowered note at either end is placed exactly
export function phraseSpan(phrase: SolfegePhrase): SemitoneSpan {
  return spanWithDo(phraseSemitones(phrase));
}

// Whether some do keeps the whole phrase, and do, inside the range as measured (untrimmed)
export function phraseFitsRange(phrase: SolfegePhrase, range: VocalRange): boolean {
  return homeNotesInsideSpan(range, phraseSpan(phrase)).length > 0;
}

// The phrase's do, by Interval Training's rule (pickHomeNote): the trimmed range first, then the
// range as measured, within the staff's limit where the range allows and without it otherwise, so a
// phrase that fits the range is always placed wholly inside it; a phrase wider than the range as
// close to it as it can be. Never `previous` when another do is as good.
export function pickPhraseHomeNote(
  range: VocalRange,
  phrase: SolfegePhrase,
  random: RandomSource = Math.random,
  previous?: number,
  within?: VocalRange,
): number {
  return pickHomeNoteForSpan(range, phraseSpan(phrase), random, previous, within);
}

// ---- A session's phrases --------------------------------------------------------------------

// The kinds of phrase in the order a level brings them in, each a tier: openings, the lines every
// churchgoer knows, to begin with; once the singer is getting reasonably good at those, last
// phrases and refrains join them; then lines from the middle of a hymn, the openings and closings
// still coming back (the product owner, issue #3772). A session serves its phrases in this order
// too, the familiar first.
export const solfegePhraseKinds: readonly SolfegePhraseKind[] = ['opening', 'closing', 'middle'];

export const solfegeTierTunables = {
  // Which tiers are open at a level comes from the singer's qualifying sessions there: sessions
  // scoring learnUnlockScore or better, which LearnModuleProgress.qualifyingSessions already counts
  // per level, so nothing new is saved. Closings join after this many, one short of the
  // learnUnlockSessions that unlock the next level, so a singer has met them before moving on;
  closingsAfter: 2,
  // and middles after this many, for a singer who keeps at a level past unlocking the next.
  middlesAfter: 4,
  // A session's phrases of each kind while one, two or all three tiers are open. Each adds up to
  // solfegePhrasesPerSession; a share a level cannot fill is filled from the other tiers (see
  // pickSolfegePhrases).
  shares: [
    { opening: 4, closing: 0, middle: 0 },
    { opening: 2, closing: 2, middle: 0 },
    { opening: 1, closing: 1, middle: 2 },
  ] as readonly Record<SolfegePhraseKind, number>[],
};

// The kinds of phrase a level's sessions serve after `qualifyingSessions` qualifying sessions there,
// in tier order
export function solfegeOpenKinds(qualifyingSessions: number): SolfegePhraseKind[] {
  const { closingsAfter, middlesAfter } = solfegeTierTunables;
  if (qualifyingSessions >= middlesAfter) {
    return ['opening', 'closing', 'middle'];
  }
  return qualifyingSessions >= closingsAfter ? ['opening', 'closing'] : ['opening'];
}

// How many phrases of each kind a session takes, in solfegePhraseKinds' order, from pools of these
// sizes: the shares of the open tiers, cut to a shorter session (a test's) from the familiar end,
// each no more than its pool holds. What a pool cannot give is made up from the other open pools,
// earliest tier first, then from the tiers not open yet, earliest first, rather than sing one
// phrase twice in a session; only when every pool together holds fewer than `count` does a session
// fall short, and pickSolfegePhrases repeats what it has.
function sessionTakes(poolSizes: number[], openTiers: number, count: number): number[] {
  const shares = solfegeTierTunables.shares[openTiers - 1];
  let left = count;
  const takes = solfegePhraseKinds.map((kind, index) => {
    const take = Math.min(shares[kind], poolSizes[index], left);
    left -= take;
    return take;
  });
  for (const index of solfegePhraseKinds.keys()) {
    const more = Math.min(left, poolSizes[index] - takes[index]);
    takes[index] += more;
    left -= more;
  }
  return takes;
}

// The `take` phrases a session takes from a pool, `take × sessionsPlayed` phrases into the pool
// served over and over. Each time round the pool comes in the deck's order, or, `shuffled`, in an
// order of its own seeded by which time round it is, so every phrase comes round once before any
// comes round again and the same session always gets the same phrases. A session's take can run
// from the end of one round into the next, so a shuffled round keeps the phrases that ended the
// round before it out of its first `take - 1` places, and a session never holds a phrase twice.
function servedFromPool(
  pool: SolfegePhrase[],
  seed: number,
  take: number,
  sessionsPlayed: number,
  shuffled: boolean,
): SolfegePhrase[] {
  if (take === 0) {
    return [];
  }
  const first = take * sessionsPlayed;
  if (!shuffled) {
    return Array.from({ length: take }, (_phrase, index) => pool[(first + index) % pool.length]);
  }
  const firstRound = Math.floor(first / pool.length);
  const lastRound = Math.floor((first + take - 1) / pool.length);
  // Each round's order depends on the one before it, so the rounds are worked out from the first
  const rounds: SolfegePhrase[][] = [];
  let previous: SolfegePhrase[] = [];
  for (let round = 0; round <= lastRound; round += 1) {
    const ended = previous.slice(pool.length - (take - 1));
    previous = keepApart(seededShuffle(pool, round * solfegePhraseKinds.length + seed), ended, take - 1);
    if (round >= firstRound) {
      rounds.push(previous);
    }
  }
  const run = rounds.flat().slice(first - firstRound * pool.length, first - firstRound * pool.length + take);
  // A pool too small to keep a round's end from its next start (fewer than 2 × (take - 1) phrases)
  // can still bring a phrase back: it is served once, the session made up from the rest of the pool
  const served = [...new Set(run)];
  return [...served, ...pool.filter((phrase) => !served.includes(phrase))].slice(0, take);
}

// `order` with any of `avoid` in its first `head` places moved to just after them, the rest kept in
// their order
function keepApart(order: SolfegePhrase[], avoid: SolfegePhrase[], head: number): SolfegePhrase[] {
  const kept: SolfegePhrase[] = [];
  const moved: SolfegePhrase[] = [];
  for (const phrase of order) {
    if (kept.length < head && avoid.includes(phrase)) {
      moved.push(phrase);
      continue;
    }
    kept.push(phrase);
    if (kept.length === head) {
      kept.push(...moved.splice(0));
    }
  }
  return [...kept, ...moved];
}

// A Fisher-Yates shuffle driven by mulberry32: the same seed always gives the same order
function seededShuffle<T>(items: readonly T[], seed: number): T[] {
  const random = mulberry32(seed);
  const shuffled = [...items];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const other = Math.floor(random() * (index + 1));
    [shuffled[index], shuffled[other]] = [shuffled[other], shuffled[index]];
  }
  return shuffled;
}

// The session's phrases (issue #3772). Only the phrases of the level's deck that fit the range are
// served, sorted by kind into a pool for each tier, and the singer's qualifying sessions at the
// level open the tiers (solfegeOpenKinds): while only openings are open a session takes four of
// them; with closings, two openings and two closings; with middles, an opening, a closing and two
// middles (solfegeTierTunables.shares), served in that order, the familiar first. A pool moves on
// through its phrases from session to session, `take × sessionsPlayed` phrases in, and wraps, so
// every phrase of a pool comes round before one repeats. While only openings are open they come in
// the deck's order, round after round, as every session served them before the tiers; once a
// second tier opens every pool comes in a fresh order each time round (seededShuffle), so the
// phrases a singer already knows do not come back in the same order every time. No phrase comes
// twice in a session unless fewer than `count` fit the range in all; a pool too small for its
// share leaves the rest to the others (sessionTakes), and a level without phrases of a kind simply
// serves its share from the others. When no phrase of the deck fits, the whole deck is served in
// order and pickPhraseHomeNote places each phrase as close to the range as it can, out of it only
// as far as the phrase must go, as Interval Training places a session wider than the range.
// Deterministic: the same range, sessions played, qualifying sessions and level always serve the
// same phrases.
export function pickSolfegePhrases(
  range: VocalRange,
  sessionsPlayed: number,
  qualifyingSessions: number,
  level: LearnLevel = LearnLevel.Easy,
  count = solfegePhrasesPerSession,
): SolfegePhrase[] {
  const deck = solfegeDeck(level);
  const fitting = deck.filter((candidate) => phraseFitsRange(candidate, range));
  if (fitting.length === 0) {
    const start = (count * sessionsPlayed) % deck.length;
    return Array.from({ length: count }, (_phrase, index) => deck[(start + index) % deck.length]);
  }
  const openTiers = solfegeOpenKinds(qualifyingSessions).length;
  const pools = solfegePhraseKinds.map((kind) => fitting.filter((phrase) => phrase.kind === kind));
  const poolSizes = pools.map((pool) => pool.length);
  const takes = sessionTakes(poolSizes, openTiers, count);
  // Past the first tier every pool comes in a fresh order each time round
  const shuffled = openTiers > 1;
  const session = pools.flatMap((pool, kindIndex) =>
    servedFromPool(pool, kindIndex, takes[kindIndex], sessionsPlayed, shuffled),
  );
  // Fewer phrases fit the range than a session holds: the session sings them again
  return Array.from({ length: count }, (_phrase, index) => session[index % session.length]);
}
